import React, { FC, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'

import { TracingReportRuleForm } from '@/apps/latest/components/molecules/TracingReportRuleForm'
import { TracingReportRuleTable } from '@/apps/latest/components/portal/TracingReportRuleTable'
import {
  describeTracingRule,
  useCreateTracingReportRuleMutation,
  useDeleteTracingReportRuleMutation,
  useTracingReportRulesQuery,
  type TracingReportRule,
} from '@/features/internal/tracing-report'
import {
  Banner,
  DialogShell,
  NotionButton,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'

type DialogMode = 'create' | 'delete' | null

/**
 * トレーシングレポート (v3, beta)。
 * 医師との取り決め (対象の医薬品グループ×新規処方 → レポート自動作成) を事前登録する独立ページ。
 * SOAPルール (文章ルール) とは意味論が別物のため同居しない (design-book 04-apps/kakeru.md)。
 */
export const TracingReportsPage: FC = () => {
  const navigate = useNavigate()
  const { data, isLoading: loading, error } = useTracingReportRulesQuery()
  const rules = data ?? []
  const createMutation = useCreateTracingReportRuleMutation()
  const deleteMutation = useDeleteTracingReportRuleMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedRule, setSelectedRule] = useState<TracingReportRule | null>(
    null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleDelete = (rule: TracingReportRule) => {
    setSelectedRule(rule)
    setDialogMode('delete')
  }

  const handleSubmit = async (drugGroupId: string) => {
    try {
      setIsSubmitting(true)
      await createMutation.mutateAsync(drugGroupId)
      setDialogMode(null)
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedRule) return
    try {
      setIsSubmitting(true)
      await deleteMutation.mutateAsync(selectedRule.id)
      setDialogMode(null)
      setSelectedRule(null)
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogMode(null)
      setSelectedRule(null)
    }
  }

  if (error) {
    return (
      <PageScaffold title="トレーシングレポート">
        <Banner severity="danger">{error.message}</Banner>
      </PageScaffold>
    )
  }

  return (
    <PageScaffold
      title="トレーシングレポート"
      meta={rules.length > 0 ? `全 ${rules.length} 件` : undefined}
      actions={
        <Stack direction="row" gap={1}>
          <NotionButton
            variant="secondary"
            onClick={() => navigate('/portal/soaps')}
          >
            医薬品グループ
          </NotionButton>
          <NotionButton
            variant="primary"
            startIcon={<AddIcon />}
            onClick={() => setDialogMode('create')}
          >
            新規ルール
          </NotionButton>
        </Stack>
      }
    >
      <Typography sx={{ ...PORTAL_TEXT.caption, mb: 2 }}>
        対象の医薬品グループが新規処方されたら自動作成し、提出の旨をSOAPに記載します
      </Typography>

      <TracingReportRuleTable
        rules={rules}
        loading={loading}
        onDelete={handleDelete}
      />

      <DialogShell
        open={dialogMode === 'create'}
        onClose={handleCloseDialog}
        title="ルール作成"
        maxWidth="sm"
        disabled={isSubmitting}
      >
        <TracingReportRuleForm
          onSubmit={handleSubmit}
          onCancel={handleCloseDialog}
          isLoading={isSubmitting}
        />
      </DialogShell>

      <DialogShell
        open={dialogMode === 'delete'}
        onClose={handleCloseDialog}
        title="ルール削除確認"
        maxWidth="xs"
        disabled={isSubmitting}
        actions={
          <>
            <NotionButton
              variant="secondary"
              onClick={handleCloseDialog}
              disabled={isSubmitting}
            >
              キャンセル
            </NotionButton>
            <NotionButton
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? '削除中…' : '削除'}
            </NotionButton>
          </>
        }
      >
        <Typography sx={PORTAL_TEXT.rowLabel}>
          「{selectedRule ? describeTracingRule(selectedRule) : ''}
          」を削除しますか？
        </Typography>
      </DialogShell>
    </PageScaffold>
  )
}

export default TracingReportsPage
